/**
 * Snapshot Formatter Shared Types and Helpers
 *
 * Shared types, role sets, and text/attribute helpers used by the snapshot
 * formatter subsystems.
 *
 * @module
 */

import type { ProbeResult } from './browser-client.interface.js';
import type { BoundingBox } from './image-ops.js';
import { isNoisyLabel, normalizeIconLabelText } from './icon-label-normalizer.js';
import { resolveLucideName } from './lucide-icon-names.js';
import type { CapturedToast } from './playwright-client/internal-types.js';
import type { SnapshotElement } from './snapshot-analyzer.js';
import type { ElementHintCategory } from '@chatsdet/types';

/**
 * A semantic section derived from the accessibility tree.
 */
export interface Section {
  ref: string;
  role: string;
  heading: string;
  level: number;
  elements: FormattedElement[];
  subsections: Section[];
  containsActive: boolean;
  collapsed: boolean;
  isBackground: boolean;
  isFocused: boolean;
  elementCount: number;
  gridInfo?: GridInfo;
  hasSearchMatch?: boolean;
  contextAnnotations?: string[];
  contentCards?: ContentCard[];
  datepickerInfo?: DatepickerInfo;
  /**
   * Ref of a React Aria dismiss-sentinel button paired to this dialog.
   * When set, the renderer surfaces a `[dismiss=<ref>]` annotation on the
   * section heading instead of emitting the sentinel as a separate ref.
   */
  dismissActionRef?: string;
  /**
   * True when this section is a dialog/alertdialog whose live-DOM container
   * is verifiably blocking the page (`aria-modal=true` or an inert/aria-hidden
   * page shell). Used by `markBackgroundSections` to suppress non-modal
   * content when a true modal is open. Never set on bare `[role=dialog]`.
   */
  isBlockingModal?: boolean;
  /**
   * True when this dialog/alertdialog was verified inert (collapsed, zero-box,
   * or off-screen) during augmentation. Such dialogs never block the page and
   * are omitted from the DIALOG STACK header — common for chat/announcer
   * widgets that leave `aria-modal=true` on while visually closed.
   */
  isInertDialog?: boolean;
}

/**
 * A non-interactive content card (e.g., calendar event, info card).
 */
export interface ContentCard {
  ref: string;
  texts: string[];
}

/**
 * A compact datepicker detected from day-header + numbered-cell patterns.
 */
export interface DatepickerInfo {
  ref: string;
  dayHeaders: string[];
  weeks: DatepickerWeek[];
}

export interface DatepickerWeek {
  days: DatepickerDay[];
}

export interface DatepickerDay {
  ref: string;
  dayNumber: number;
  isSelected: boolean;
}

/**
 * A formatted element ready for output.
 */
export interface FormattedElement {
  ref: string;
  role: string;
  name: string;
  fieldLabel?: string;
  label?: string;
  value?: string;
  status?: string;
  inputValue?: string;
  options?: string[];
  isActive: boolean;
  isDisabled: boolean;
  isReadonly: boolean;
  isClickable: boolean;
  attributes: string[];
  /** Hint frontmatter text to render inline (e.g., "opens modal with name/tags fields") */
  hint?: string;
  /** Hint category for rendering prefix (element, format, pattern, guidance) */
  hintCategory?: ElementHintCategory;
  /** Whether the hint has an expanded body available via hintDetail */
  hintHasBody?: boolean;
  /** Whether the hint was anchored to a parent (true) or attached directly (false) */
  hintAnchoredToParent?: boolean;
  /** Description of the element the hint refers to (used when anchored to parent) */
  hintElement?: string;
  /**
   * Substrate-v2: render-only decoration (e.g. duplicate-label disambiguator
   * suffix like ` (1)`). Substrate fields stay canonical — this carries
   * presentation-only changes the renderer needs without polluting the
   * substrate-derived `name`/`label` fields.
   *
   * Read-side render code prefers `displayDecoration` over equivalent
   * mutations of `name`/`label` when both could be used. v1 path leaves
   * this undefined and continues to mutate `name`/`label` directly so
   * existing snapshots remain byte-identical.
   */
  displayDecoration?: string;
}

/**
 * Result of data extraction from button/link text.
 */
interface ExtractedData {
  label: string;
  value?: string;
  status?: string;
}

/**
 * Grid information extracted from grid/treegrid elements.
 */
export interface GridInfo {
  ref: string;
  columns: GridColumn[];
  rows: GridRow[];
  headerRowRef: string | null;
  totalRows: number;
  containsActive: boolean;
  columnByPosition?: Map<number, string>;
  headerChildCount?: number;
  headerCellPositions?: number[];
}

/**
 * A column in a grid with its clickable ref (for sorting).
 */
export interface GridColumn {
  name: string;
  ref: string;
}

/**
 * A row in a grid.
 */
export interface GridRow {
  ref: string;
  index: number;
  label?: string;
  isSelected: boolean;
  cells: GridCell[];
  totalChildren?: number;
  cellPositions?: number[];
  rowActions?: Array<{ ref: string; label: string }>;
  expandedContent?: {
    labels: string[];
    buttons: Array<{ ref: string; label: string }>;
  };
}

/**
 * A cell in a grid row.
 */
export interface GridCell {
  ref: string;
  column: string;
  value: string;
  hasValue: boolean;
  gridcellLabel?: string;
  positionInRow?: number;
  role?: string;
  /** DOM-grounded `data-field` token, for the row-scoped locator anchor. */
  dataField?: string;
  /**
   * Cell is editable but currently empty — surfaced so the agent can target
   * it by ref instead of falling back to coordinate clicks.
   */
  isEditable?: boolean;
}

// BoundingBox kept file-local (imported from image-ops.js above)

export type SearchMatchContext = 'focused' | 'foreground' | 'background';

export interface SearchMatch {
  ref: string;
  role: string;
  label: string;
  term: string;
  isInteractive: boolean;
  index?: number;
  boundingBox?: BoundingBox;
  centerX?: number;
  centerY?: number;
  context?: SearchMatchContext;
  sectionPath?: string;
}

/** Maximum number of search matches to return */
export const MAX_SEARCH_MATCHES = 10;

export interface SearchResult {
  matches: SearchMatch[];
  totalFound: number;
}

export interface SemanticSnapshotOptions {
  searchMatches?: SearchMatch[];
  totalMatchCount?: number;
  snapshotFilePath?: string;
  probeResult?: ProbeResult;
  currentTime?: string;
  capturedToasts?: CapturedToast[];
  /** New console errors since last snapshot (shown once). */
  consoleErrors?: Array<{ text: string }>;
  /** New failed network requests since last snapshot (shown once). */
  failedRequests?: Array<{
    method: string;
    url: string;
    status: number | 'pending';
    statusText: string;
  }>;
  /** Element hints keyed by fingerprint hash — injected from the DB layer */
  elementHints?: Map<string, ElementHintAnnotation>;
  /**
   * Output: populated by formatSemanticSnapshot with the IDs of hints that
   * matched elements in this snapshot. The caller uses this to record matches
   * via HintProvider.recordMatches().
   */
  matchedHintIds?: string[];
}

/**
 * A hint annotation ready for injection into the snapshot.
 * Passed in via SemanticSnapshotOptions from the API layer.
 */
export interface ElementHintAnnotation {
  id: string;
  category: ElementHintCategory;
  element: string;
  frontmatter: string;
  hasBody: boolean;
}

export const INTERACTIVE_LEAF_ROLES = new Set([
  'button',
  'link',
  'textbox',
  'checkbox',
  'radio',
  'combobox',
  'option',
  'menuitem',
  'tab',
  'slider',
  'spinbutton',
  'searchbox',
  'switch',
  'svg-shape',
]);

export const FORM_INPUT_ROLES = new Set([
  'combobox',
  'textbox',
  'searchbox',
  'spinbutton',
  'slider',
]);

const INTERACTIVE_CONTAINER_ROLES = new Set(['menu', 'menubar', 'tablist', 'listbox']);

export const INTERACTIVE_ROLES = new Set([
  ...INTERACTIVE_LEAF_ROLES,
  ...INTERACTIVE_CONTAINER_ROLES,
]);

export const STRUCTURAL_ROLES = new Set([
  'banner',
  'navigation',
  'main',
  'contentinfo',
  'complementary',
  'dialog',
  'alertdialog',
  'form',
  'region',
  'article',
  'section',
  'iframe',
]);

export const TEXT_CARRYING_ROLES = new Set([
  'generic',
  'paragraph',
  'heading',
  'strong',
  'emphasis',
  'text',
  'blockquote',
  'caption',
]);

export const VISUAL_HEADING_MAX_LENGTH = 60;

const STATUS_WORDS = new Set([
  'favorable',
  'unfavorable',
  'success',
  'error',
  'warning',
  'pending',
]);

const BLANK_PATTERNS = [/\s*\(Blank\)\s*/gi, /\s*\(blank\)\s*/gi];

const MENU_TRIGGER_PATTERNS = [/^Open menu for /i, /^Show more options$/i];

export const SECTION_BOUNDARY_ROLES = new Set([
  'dialog',
  'alertdialog',
  'form',
  'main',
  'region',
  'article',
  'section',
  'complementary',
]);

const FRAMEWORK_NOISE_RE =
  /^(ng-binding|ng-scope|ng-pristine|ng-dirty|ng-valid|ng-invalid|ng-untouched|ng-touched|v-enter|v-leave)$/;

export function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[-_.,;:!?'"()[\]{}/\\@#$%^&*+=<>~`|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isActiveElement(element: SnapshotElement): boolean {
  return element.attributes['active'] !== undefined || element.rawLine.includes('[active]');
}

export function isDisabledElement(element: SnapshotElement): boolean {
  return element.attributes['disabled'] !== undefined || element.rawLine.includes('[disabled]');
}

export function isReadonlyElement(element: SnapshotElement): boolean {
  return element.attributes['readonly'] !== undefined || element.rawLine.includes('[readonly]');
}

export function isClickableByAttribute(element: SnapshotElement): boolean {
  return element.attributes['cursor'] === 'pointer';
}

export function isActionableClickableGeneric(element: SnapshotElement): boolean {
  return isClickableByAttribute(element) && !INTERACTIVE_ROLES.has(element.role);
}

export function isSearchInteractiveElement(element: SnapshotElement): boolean {
  return INTERACTIVE_LEAF_ROLES.has(element.role) || isActionableClickableGeneric(element);
}

export function getElementText(el: SnapshotElement): string | undefined {
  if (el.text && el.inputValue && FRAMEWORK_NOISE_RE.test(el.text.trim())) {
    return el.inputValue;
  }
  return el.text || el.inputValue;
}

function composeLabel(element: SnapshotElement): string {
  const directText = getElementText(element);
  if (directText) return directText;

  const textParts: string[] = [];

  function collectText(el: SnapshotElement, depth: number): void {
    if (depth > 3) return;
    const text = getElementText(el);
    if (text && TEXT_CARRYING_ROLES.has(el.role)) {
      textParts.push(text);
    }
    for (const child of el.children) {
      if (!INTERACTIVE_LEAF_ROLES.has(child.role)) {
        collectText(child, depth + 1);
      }
    }
  }

  for (const child of element.children) {
    if (!INTERACTIVE_LEAF_ROLES.has(child.role)) {
      collectText(child, 0);
    }
  }

  return textParts.join(' | ') || element.role;
}

function composeChildLabel(element: SnapshotElement): string | null {
  const textParts: string[] = [];

  function collectText(el: SnapshotElement, depth: number): void {
    if (depth > 3) return;
    const text = getElementText(el);
    if (text && TEXT_CARRYING_ROLES.has(el.role)) {
      textParts.push(text);
    }
    for (const child of el.children) {
      if (!INTERACTIVE_LEAF_ROLES.has(child.role)) {
        collectText(child, depth + 1);
      }
    }
  }

  for (const child of element.children) {
    if (!INTERACTIVE_LEAF_ROLES.has(child.role)) {
      collectText(child, 0);
    }
  }

  return textParts.length > 0 ? textParts.join(' | ') : null;
}

export function getDisplayText(
  element: SnapshotElement,
  options?: { overrideLabel?: string }
): string {
  const isClickable = isActionableClickableGeneric(element);
  const directElementText = FORM_INPUT_ROLES.has(element.role)
    ? (element.text ?? '')
    : (getElementText(element) ?? '');
  // Substrate-v2: when the element carries an augmented name paired with
  // an augmented role (e.g. modal `<div>` whose accessible name was
  // derived from a heading at capture time), prefer that over the native
  // text. v1 elements never set `augmentedName`/`role-src` so this is
  // a no-op for them.
  const augmentedName =
    element.augmentedName !== undefined &&
    element.attributeSources?.['role'] === 'augmented:dom-mutation'
      ? element.augmentedName
      : undefined;
  let rawTextSource =
    options?.overrideLabel ??
    augmentedName ??
    (isClickable ? composeLabel(element) : directElementText);

  if (rawTextSource && isNoisyLabel(rawTextSource) && INTERACTIVE_LEAF_ROLES.has(element.role)) {
    const childText = composeChildLabel(element);
    rawTextSource = childText ?? '';
  }

  if (!rawTextSource && INTERACTIVE_LEAF_ROLES.has(element.role)) {
    rawTextSource = composeChildLabel(element) ?? '';
  }

  // Render-time icon translation: when an icon-only control has no
  // accessible name but the substrate captured a DOM-grounded `iconClass`
  // (e.g. `lucide-sliders-horizontal`), surface the friendly UI verb
  // ("Filter") in the rendered snapshot. The substrate stays anchored to
  // the actual class so locator predicates can verify the same element.
  if (!rawTextSource && element.iconClass && INTERACTIVE_LEAF_ROLES.has(element.role)) {
    return resolveLucideName(element.iconClass);
  }

  // Render-time img translation: an unlabeled `<img>` that lacks alt /
  // title / aria-label still has its `src` on the substrate. Derive a
  // human label from the filename (last path segment, no extension) so
  // the agent has something to reason over. Locator predicates anchor
  // on `img[src*="<filename>"]` independently — the friendly label is
  // render-only.
  if (!rawTextSource && element.role === 'img') {
    const friendly = friendlyLabelFromSrc(element.attributes['src']);
    if (friendly) return friendly;
  }

  return normalizeIconLabelText(element.role, rawTextSource);
}

/**
 * Convert a `src` URL into a short human-readable label. Returns
 * undefined for empty / data: URLs / values too short to read. Mirrors
 * the legacy unlabeled-clickable-image filename derivation but as a
 * render concern, not a substrate one.
 */
function friendlyLabelFromSrc(src: string | undefined): string | undefined {
  if (!src) return undefined;
  if (src.startsWith('data:')) return undefined;
  let cleaned = src.split('?')[0].split('#')[0];
  const slash = cleaned.lastIndexOf('/');
  if (slash >= 0) cleaned = cleaned.slice(slash + 1);
  // Drop file extension.
  const dot = cleaned.lastIndexOf('.');
  if (dot > 0) cleaned = cleaned.slice(0, dot);
  cleaned = cleaned.trim();
  if (cleaned.length < 3 || cleaned.length > 50) return undefined;
  return cleaned;
}

export function hasInteractiveDescendants(element: SnapshotElement): boolean {
  if (element.ref && (INTERACTIVE_ROLES.has(element.role) || isClickableByAttribute(element)))
    return true;
  for (const child of element.children) {
    if (hasInteractiveDescendants(child)) return true;
  }
  return false;
}

export function subtreeContainsActive(element: SnapshotElement): boolean {
  if (isActiveElement(element)) return true;
  for (const child of element.children) {
    if (subtreeContainsActive(child)) return true;
  }
  return false;
}

/**
 * React Aria's `<ModalOverlay>` injects a focus-trap button with
 * `aria-label="Dismiss"`, no children, no visible text, sibling of the
 * dialog. It dismisses the modal when clicked but is meaningless to a
 * pointer user as a standalone control. Identifying it lets the formatter
 * promote it from a free-floating button to a dismiss action attached to
 * its dialog.
 *
 * Detection is structural-only — empty button whose accessible name is
 * exactly "Dismiss". Pairing to a specific dialog is the caller's job.
 */
export function isAriaDismissSentinel(element: SnapshotElement): boolean {
  if (element.role !== 'button') return false;
  if (element.children.length !== 0) return false;
  const name = element.text?.trim();
  return name === 'Dismiss';
}

function cleanLabel(text: string): string {
  return text.replace(/\s+/g, ' ').replace(/…+/g, '...').trim();
}

export function isBlankValue(value: string | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim().toLowerCase();
  return trimmed === '' || trimmed === '(blank)' || trimmed === 'blank';
}

export function stripBlankPatterns(text: string): string {
  let result = text;
  for (const pattern of BLANK_PATTERNS) {
    result = result.replace(pattern, ' ');
  }
  return result.replace(/\s+/g, ' ').trim();
}

export function extractDataValue(text: string): ExtractedData {
  if (!text) return { label: '' };

  let cleaned = text.replace(/\s+See more\s*$/i, '').trim();
  cleaned = stripBlankPatterns(cleaned);

  let status: string | undefined;
  for (const statusWord of STATUS_WORDS) {
    const statusRegex = new RegExp(`\\b(${statusWord})\\b`, 'i');
    const statusMatch = cleaned.match(statusRegex);
    if (statusMatch) {
      status = statusMatch[1];
      cleaned = cleaned.replace(statusRegex, '').trim();
    }
  }

  const currencyMatch = cleaned.match(/[$€£¥]\s*[\d,]+(?:\.\d{2})?/);
  if (currencyMatch) {
    const value = currencyMatch[0].replace(/\s/g, '');
    const label = cleaned.replace(currencyMatch[0], '').trim();
    return { label: cleanLabel(label), value: isBlankValue(value) ? undefined : value, status };
  }

  const numericEndMatch = cleaned.match(/\s+([\d,]+(?:\.\d+)?(?:\+)?|-)\s*$/);
  if (numericEndMatch) {
    const value = numericEndMatch[1];
    const label = cleaned.replace(numericEndMatch[0], '').trim();
    return { label: cleanLabel(label), value: isBlankValue(value) ? undefined : value, status };
  }

  return { label: cleanLabel(cleaned), status };
}

export function isMenuTriggerNoise(element: SnapshotElement): boolean {
  if (isActiveElement(element)) return false;
  if (element.role !== 'button') return false;

  const text = element.text || '';
  if (!text.trim()) return false;

  for (const pattern of MENU_TRIGGER_PATTERNS) {
    if (pattern.test(text)) return true;
  }

  return false;
}

export function countElementsWithRefs(element: SnapshotElement): number {
  let count = element.ref ? 1 : 0;
  for (const child of element.children) {
    count += countElementsWithRefs(child);
  }
  return count;
}

export function collectComboboxOptions(element: SnapshotElement): string[] | undefined {
  const options: string[] = [];

  function walk(el: SnapshotElement): void {
    if (el.role === 'option' && el.text?.trim()) {
      options.push(el.text.trim());
    }
    for (const child of el.children) {
      walk(child);
    }
  }

  for (const child of element.children) {
    walk(child);
  }

  return options.length > 0 ? options : undefined;
}
